import { useMemo, useState } from "react";
import type { FeatureCollection } from "geojson";
import { useBairros, useBairrosGeojson } from "@/hooks";
import PageHeader from "@/components/shared/PageHeader";
import type { Bairro } from "@/types/bairros";
import BairrosMap from "./components/BairrosMap";
import BairroDetail from "./components/BairroDetail";
import { INDICATORS, buildLegend } from "./indicators";

const REGIONS = ["CENTRO OESTE", "LESTE", "NOROESTE", "NORTE", "OESTE", "SUDOESTE", "SUL"];
const pretty = (r: string) => r.charAt(0) + r.slice(1).toLowerCase();
const GROUPS = ["Exemplares arbóreos", "Resiliência territorial"] as const;

export default function TerritorioBairrosPage() {
  const { data: bairros, loading: loadingList, error: listError } = useBairros();
  const { data: geo, loading: loadingGeo, error: geoError } = useBairrosGeojson();

  const [indicatorId, setIndicatorId] = useState("canopy");
  const [region, setRegion] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const indicator = INDICATORS.find((i) => i.id === indicatorId) ?? INDICATORS[0];
  const byId = useMemo(() => new Map((bairros ?? []).map((b) => [b.id, b])), [bairros]);

  const measured = useMemo(() => {
    const rows: Array<{ bairro: Bairro; value: number }> = [];
    for (const b of bairros ?? []) {
      if (region && b.region !== region) continue;
      const v = indicator.value(b);
      if (v != null) rows.push({ bairro: b, value: v });
    }
    return rows;
  }, [bairros, indicator, region]);

  const measuredIds = useMemo(() => new Set(measured.map((r) => r.bairro.id)), [measured]);

  const legend = useMemo(
    () => buildLegend(measured.map((r) => r.value), indicator.direction),
    [measured, indicator],
  );

  const ranking = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? measured.filter((r) => r.bairro.name.toLowerCase().includes(q)) : measured;
    return [...rows].sort((a, b) => b.value - a.value).slice(0, 8);
  }, [measured, query]);

  const summary = useMemo(() => {
    const all = bairros ?? [];
    return {
      total: all.length,
      withTrees: all.filter((b) => b.treeCount > 0).length,
      withTerritorial: all.filter((b) => b.analyzedBlocks > 0).length,
      trees: all.reduce((s, b) => s + b.treeCount, 0),
    };
  }, [bairros]);

  const selected = selectedId ? (byId.get(selectedId) ?? null) : null;
  const loading = loadingList || loadingGeo;
  const error = listError || geoError;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Bairros"
        subtitle="Exemplares arbóreos, resiliência e monitoramento nos bairros oficiais de Goiânia"
      />

      {error ? (
        <div className="rounded-card border border-danger-200 bg-danger-50/60 p-5 text-sm text-danger-800">
          Não foi possível carregar os bairros. {error}
        </div>
      ) : (
        <>
          {/* Cobertura dos dados: o que o mapa pode ou não afirmar */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-card border border-background-200 bg-white px-4 py-3 text-sm text-foreground-600">
            <span>
              <strong className="text-foreground-900 tabular-nums">{summary.total.toLocaleString("pt-BR")}</strong>{" "}
              bairros oficiais
            </span>
            <span>
              <strong className="text-foreground-900 tabular-nums">{summary.withTrees}</strong> com exemplares
              cadastrados ({summary.trees.toLocaleString("pt-BR")} árvores)
            </span>
            <span>
              <strong className="text-foreground-900 tabular-nums">{summary.withTerritorial}</strong> com quadras
              analisadas
            </span>
            <span className="text-foreground-500">Bairro sem dado fica apagado no mapa: não medido não é zero.</span>
          </div>

          {/* Indicador e região */}
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1 space-y-2">
              {GROUPS.map((group) => (
                <div key={group} className="flex flex-wrap items-center gap-2">
                  <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    {group}
                  </span>
                  {INDICATORS.filter((i) => i.group === group).map((i) => (
                    <button
                      key={i.id}
                      onClick={() => setIndicatorId(i.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                        i.id === indicator.id
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <label className="block lg:w-52">
              <span className="text-xs font-medium text-foreground-700">Região</span>
              <select
                value={region ?? ""}
                onChange={(e) => setRegion(e.target.value || null)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm bg-white"
              >
                <option value="">Todas as regiões</option>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {pretty(r)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            {/* Mapa */}
            <div className="xl:col-span-2">
              <div className="relative h-[520px] rounded-card overflow-hidden border border-background-200 bg-background-50">
                {loading || !geo || !bairros ? (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-foreground-500">
                    <i className="ri-loader-4-line animate-spin mr-2" /> Carregando bairros...
                  </div>
                ) : (
                  <BairrosMap
                    collection={geo as FeatureCollection}
                    byId={byId}
                    indicator={indicator}
                    legend={legend}
                    region={region}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    measuredIds={measuredIds}
                  />
                )}

                {/* Legenda */}
                <div className="absolute bottom-3 left-3 z-[500] max-w-[260px] rounded-lg bg-white/95 border border-background-200 px-3 py-2 shadow-sm">
                  <p className="text-xs font-semibold text-foreground-900">{indicator.label}</p>
                  <p className="text-[11px] text-foreground-500 leading-snug">{indicator.hint}</p>
                  {legend ? (
                    <div className="mt-1.5">
                      <div className="flex h-2.5 overflow-hidden rounded">
                        {legend.colors.map((c) => (
                          <div key={c} className="flex-1" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <div className="flex justify-between text-[10px] text-foreground-600 tabular-nums mt-0.5">
                        <span>{indicator.format(legend.breaks[0])}</span>
                        <span>{indicator.format(Math.max(...measured.map((r) => r.value)))}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-[11px] text-foreground-500">
                      Nenhum bairro com este indicador{region ? " nesta região" : ""}.
                    </p>
                  )}
                </div>
              </div>
              {indicator.caveat && (
                <p className="mt-2 text-xs text-foreground-500 flex gap-1.5">
                  <i className="ri-information-line mt-0.5" />
                  {indicator.caveat}
                </p>
              )}
            </div>

            {/* Painel lateral */}
            <div className="space-y-5">
              {selected && <BairroDetail bairro={selected} onClose={() => setSelectedId(null)} />}

              <section className="bg-white rounded-card border border-background-200 p-5">
                <h3 className="text-base font-semibold text-foreground-900 font-heading">Maiores valores</h3>
                <p className="text-xs text-foreground-500 mt-0.5 mb-3">
                  {indicator.label}
                  {region ? ` · região ${pretty(region)}` : ""}
                </p>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar bairro"
                  className="w-full mb-3 px-3 py-2 rounded-lg border border-background-200 text-sm"
                />
                {ranking.length === 0 ? (
                  <p className="text-sm text-foreground-500 py-4 text-center">
                    {query
                      ? "Nenhum bairro com esse nome e dado neste indicador."
                      : "Nenhum bairro com dado para este indicador."}
                  </p>
                ) : (
                  <ul className="divide-y divide-background-100">
                    {ranking.map(({ bairro, value }) => (
                      <li key={bairro.id}>
                        <button
                          onClick={() => setSelectedId(bairro.id)}
                          className={`w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg text-left cursor-pointer hover:bg-background-50 ${
                            bairro.id === selectedId ? "bg-primary-50" : ""
                          }`}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-foreground-900 truncate">{bairro.name}</span>
                            <span className="block text-xs text-foreground-500">
                              {bairro.region ? pretty(bairro.region) : "Sem região"}
                            </span>
                          </span>
                          <span className="text-sm font-semibold text-foreground-900 tabular-nums">
                            {indicator.format(value)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
